/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/12/22
 */

import React from "react";
import {
	BeachAccess, LocalHospital, Computer, School, ChildFriendly, BusinessCenter, HeadsetMic, PowerSettingsNew,
	Event, Home, Business, HomeWork, Warning, MedicalServices, Flight, Group, LocalPolice, LocalFireDepartment,
	PriorityHigh, RocketLaunch, Groups, LocalLibrary, SwapHoriz, Sailing, Grading, Assignment, LibraryBooks, PersonPin,
	PermContactCalendar, RateReview, QuestionAnswer
} from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faDove, faStairs, faChess} from "@fortawesome/free-solid-svg-icons";
import {EventType} from "../interfaces/Global";

export interface EventTypeOption extends EventType {
	icon?: React.ReactNode
}

export const eventTypes: EventTypeOption[] = [
	{id: "PTO", name: "Planned Time Off"},
	{id: "AL", name: "Annual Leave", parentId: "PTO", isUnavailable: true, icon: <BeachAccess fontSize={"small"} />},
	{id: "PL", name: "Parental Leave", parentId: "PTO", isUnavailable: true, icon: <ChildFriendly fontSize={"small"} />},
	{id: "ML", name: "Medical Leave", parentId: "PTO", isUnavailable: true, icon: <MedicalServices fontSize={"small"} />},
	{id: "CMP", name: "Compensation Leave", parentId: "PTO", isUnavailable: true, icon: <SwapHoriz fontSize={"small"} />},
	{id: "TRV", name: "Business Travel", parentId: "PTO", isUnavailable: true, icon: <Flight fontSize={"small"} />},
	{id: "TO", name: "Time Off", parentId: "PTO", isUnavailable: true, icon: <PowerSettingsNew fontSize={"small"} />},
	{id: "WO", name: "Weekly Off", parentId: "PTO", isUnavailable: true, icon: <Event fontSize={"small"} />},
	{id: "OOO", name: "Out Of Office", parentId: "PTO", isUnavailable: true, icon: <Event fontSize={"small"} />},
	{id: "AFK", name: "Away From Keyboard", parentId: "PTO", isUnavailable: true, icon: <Event fontSize={"small"} />},
	{id: "BB", name: "Big Break", parentId: "PTO", isUnavailable: true, icon: <Sailing fontSize={"small"} />},
	{id: "SCD", name: "CSS Seasonal Comp Day", parentId: "PTO", isUnavailable: true, icon: <SwapHoriz fontSize={"small"} />},

	{id: "UTO", name: "Unplanned Time Off"},
	{id: "S", name: "Sick Leave", parentId: "UTO", isUnavailable: true, icon: <LocalHospital fontSize={"small"} />},
	{id: "E", name: "Emergency", parentId: "UTO", isUnavailable: true, icon: <Warning fontSize={"small"} />},
	{id: "U", name: "Unavailable", parentId: "UTO", isUnavailable: true, icon: <Event fontSize={"small"} />},

	{id: "ACT", name: "Activities"},
	{id: "TRN", name: "Training", parentId: "ACT", isUnavailable: true, icon: <School fontSize={"small"} />},
	{id: "ONB", name: "Onboarding", parentId: "ACT", isUnavailable: true, icon: <FontAwesomeIcon fixedWidth icon={faStairs} />},
	{id: "PRJ", name: "Project", parentId: "ACT", isUnavailable: true, icon: <LocalPolice fontSize={"small"} />},
	{id: "KCS", name: "Knowledge-Centered Support", parentId: "ACT", isUnavailable: true, icon: <LocalLibrary fontSize={"small"} />},
	{id: "SHA", name: "Shadowing", parentId: "ACT", isUnavailable: true, icon: <Event fontSize={"small"} />},
	{id: "SEC", name: "Secondment", parentId: "ACT", isUnavailable: true, icon: <BusinessCenter fontSize={"small"} />},
	{id: "D", name: "Disturbed", parentId: "ACT", isUnavailable: true, icon: <PriorityHigh fontSize={"small"} />},
	{id: "ESC", name: "Escalation", parentId: "ACT", isUnavailable: true, icon: <LocalFireDepartment fontSize={"small"} />},
	{id: "IM", name: "Internal Meeting", parentId: "ACT", isUnavailable: true, icon: <Group fontSize={"small"} />},
	{id: "EM", name: "External Meeting", parentId: "ACT", isUnavailable: true, icon: <Group fontSize={"small"} />},
	{id: "QM", name: "Queue Management", parentId: "ACT", isUnavailable: true, icon: <FontAwesomeIcon fixedWidth icon={faChess} />},
	{id: "MOD", name: "Manager On Duty", parentId: "ACT", isUnavailable: true, icon: <PersonPin fontSize={"small"} />},
	{id: "BSY", name: "Busy", parentId: "ACT", isUnavailable: true, icon: <Event fontSize={"small"} />},
	{id: "LRT", name: "Long Running Ticket Review", parentId: "ACT", isUnavailable: true, icon: <Assignment fontSize={"small"} />},
	{id: "TA", name: "Technical Assessment", parentId: "ACT", isUnavailable: true, icon: <Grading fontSize={"small"} />},
	{id: "OOC", name: "OOO Review Cohort", parentId: "ACT", isUnavailable: true, icon: <LibraryBooks fontSize={"small"} />},
	{id: "COA", name: "Coaching", parentId: "ACT", isUnavailable: true, icon: <School fontSize={"small"} />},
	{id: "REC", name: "Recruiting", parentId: "ACT", isUnavailable: true, icon: <PermContactCalendar fontSize={"small"} />},
	{id: "CR", name: "Case Review", parentId: "ACT", isUnavailable: true, icon: <RateReview fontSize={"small"} />},
	{id: "CHT", name: "Chat", parentId: "ACT", isUnavailable: true, icon: <QuestionAnswer fontSize={"small"} />},
	{id: "CSB", name: "Chat Standby", parentId: "ACT", isUnavailable: true, icon: <QuestionAnswer fontSize={"small"} />},

	{id: "A", name: "Available"},
	{id: "WFO", name: "Working From Office", parentId: "A", isUnavailable: false, icon: <Business fontSize={"small"} />},
	{id: "WFH", name: "Working From Home", parentId: "A", isUnavailable: false, icon: <Home fontSize={"small"} />},
	{id: "WR", name: "Working Remotely", parentId: "A", isUnavailable: false, icon: <HomeWork fontSize={"small"} />},
	{id: "OC", name: "On Call", parentId: "A", isUnavailable: false, icon: <HeadsetMic fontSize={"small"} />},
	{id: "WS", name: "Weekend Support", parentId: "A", isUnavailable: false, icon: <Computer fontSize={"small"} />},

	{id: "EVT", name: "Events"},
	{id: "TE", name: "Team Event", parentId: "EVT", isUnavailable: true, icon: <Groups fontSize={"small"} />},
	{id: "CE", name: "Company Event", parentId: "EVT", isUnavailable: true, icon: <Event fontSize={"small"} />},
	{id: "FE", name: "Foundation Event", parentId: "EVT", isUnavailable: true, icon: <FontAwesomeIcon fixedWidth icon={faDove} />},
	{id: "SHP", name: "ShipIt", parentId: "EVT", isUnavailable: true, icon: <RocketLaunch fontSize={"small"} />},
	{id: "SP", name: "Special Event", parentId: "EVT", isUnavailable: true, icon: <Event fontSize={"small"} />},
];

export const getEventType = (id: string): EventTypeOption | null => {
	const eventType = eventTypes.find(t => t.id === id);
	if (eventType) {
		const parent = eventTypes.find(t => t.id === eventType.parentId);
		if (parent) {
			return { ...eventType, parent } as EventType;
		}
		return eventType;
	}
	return null;
}